import { brDate } from "../../format";
import { fmtBRLcompactSigned, fmtNum } from "../../utils/formatters";
import { CoberturaHint } from "../ui/CoberturaHint";
import { useCurvaPnl } from "../../hooks/useCurvaPnl";
import { useDv01Ipca } from "../../hooks/useDv01Ipca";
import { useZeusStatus } from "../../hooks/useZeusStatus";
import { CurvaStaleBadge } from "../ui/CurvaStaleBadge";
import { AsOfBadge } from "../ui/AsOfBadge";
import { CurvaTable } from "./CurvaTable";
import { MetricChip } from "./MetricChip";
import { PosicaoDecomposta } from "./PosicaoDecomposta";
import { ResultadoBookBrasil } from "./ResultadoBookBrasil";

// #29: cor pelo sinal do DV01/carrego/surpresa (verde + / vermelho −).
function corDv01(v: number): string {
  if (Math.abs(v) < 0.5) return "text-slate-500";
  return v > 0 ? "text-indra-turquesa" : "text-red-400";
}

export function PnlAtacado() {
  // #55: Curva Curta/Longa vivas (D0 + MWM/MWH). Δ/PnL = "—" até o 2º snapshot
  // (d1Pendente). FAIL-CLOSED (incidente 02/08): erro/carregando NUNCA caem no
  // mock — a tela diz o estado; número inventado com cara de real não existe.
  const curvas = useCurvaPnl();
  // #29: DV01 do IPCA (sensibilidade à implícita #218) — popula DV01 Total/Next
  // + a tabela por ano. null enquanto carrega/erro → "—" honesto.
  const dv01 = useDv01Ipca().data;
  // #40: badge de curva stale (as-of do dado, nao idade do cron).
  const curvaDs = useZeusStatus().data?.datasets.curva_forward ?? null;

  if (curvas.error) {
    return (
      <div className="space-y-6">
        <h2 className="text-xl font-black text-white font-sans">PnL Indra · Book Brasil</h2>
        <div
          className="border border-red-400/30 bg-red-400/5 rounded-lg px-4 py-3 text-sm text-red-200"
          data-testid="curva-pnl-erro"
        >
          <p>Não foi possível carregar o PnL — a API não respondeu.</p>
          <button
            type="button"
            onClick={curvas.retry}
            className="mt-2 px-3 py-1.5 rounded-md border border-white/20 text-white/80 hover:bg-white/10 text-sm"
          >
            Tentar de novo
          </button>
        </div>
      </div>
    );
  }

  if (curvas.isLoading) {
    return (
      <div className="space-y-6">
        <h2 className="text-xl font-black text-white font-sans">PnL Indra · Book Brasil</h2>
        <p className="text-white/50 text-sm" data-testid="curva-pnl-carregando">
          Carregando dados do PnL…
        </p>
      </div>
    );
  }

  const curtaRows = curvas.data.curta.linhas;
  const longa = curvas.data.longa;
  const longaLive = longa.linhas.length > 0;

  // #57/#28: FV + PV do Book Brasil vivos (cél-a-cél / descontado #218). O card
  // Resultado virou bespoke (#251): sparkline de tendência + linhas FV/VP limpas,
  // sem a coluna IPCA OFF morta (toggle Nominal·Indexado = fase 2 c/ o IPCA ON).
  const res = curvas.data.resultado;
  const dataRef = res.as_of ? brDate(res.as_of) : "—";

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-xl font-black text-white font-sans">PnL Indra · Book Brasil</h2>
          <CurvaStaleBadge stale={curvaDs?.curva_stale} asOf={curvaDs?.data_ref_zeus} />
        </div>
        <p className="text-white/50 text-sm mt-1">referência {dataRef}</p>
        {/* #57: tags de book — Total vivo; Atacado/Varejo (split #60) em breve. */}
        <div className="flex gap-2 mt-2" data-testid="pnl-book-tags">
          <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-400/15 text-emerald-200 border border-emerald-300/30">
            Total · vivo
          </span>
          <span className="px-2 py-0.5 rounded text-[11px] bg-white/5 text-white/40 border border-white/10">
            Atacado · em breve
          </span>
          <span className="px-2 py-0.5 rounded text-[11px] bg-white/5 text-white/40 border border-white/10">
            Varejo · em breve
          </span>
        </div>
      </div>

      {/* [A] Hero: card Resultado redesenhado (#251) — sparkline de tendência +
          linhas Δ com FV/VP e chip de desconto a VP. */}
      <ResultadoBookBrasil resultado={res} />

      {/* [B] Métricas de risco — #58: Duration REAL (Macaulay); #29: DV01 do IPCA
          (sensibilidade à implícita #218). DV01 Total = sensibilidade à curva (R$/
          bp); DV01 Next = carrego do próx. mês (principal) + surpresa/bp ao lado.
          null (carregando/erro) → "—" honesto. */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <MetricChip
          label="Duration"
          value={res.duration !== null ? `${fmtNum(res.duration)} anos` : "—"}
        />
        <MetricChip
          label="DV01 Total (curva)"
          value={dv01 ? `${fmtBRLcompactSigned(dv01.dv01_curva_total)}/bp` : "—"}
          tone={dv01 ? (dv01.dv01_curva_total >= 0 ? "positive" : "negative") : "neutral"}
          hint={
            dv01
              ? "+1bp na inflação implícita #218 ⇒ +valor (Indra vendedora de indexado = comprada em inflação)"
              : undefined
          }
        />
        {/* DV01 Next: carrego (principal) + surpresa/bp ao lado — layout do mock. */}
        <div
          className="h-16 px-3 py-2 border border-slate-700/40 rounded-md flex flex-col justify-center bg-indra-azul-surface"
          title="Carrego = P&L determinístico do próx. mês se o IPCA realizar na implícita; Surpresa = sensibilidade ao IPCA realizado diferir do esperado"
          data-testid="dv01-next-chip"
        >
          <div className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">
            DV01 Next (carrego · surpresa)
          </div>
          {dv01 ? (
            <div className="flex items-baseline gap-2 whitespace-nowrap">
              <span className={`text-base font-semibold ${corDv01(dv01.carrego_total)}`}>
                {fmtBRLcompactSigned(dv01.carrego_total)}
              </span>
              <span className="text-[11px] text-slate-400">
                surpresa {fmtBRLcompactSigned(dv01.surpresa_total)}/bp
              </span>
            </div>
          ) : (
            <div className="text-base font-semibold text-slate-100">—</div>
          )}
        </div>
      </div>

      {/* #55: D-1/Δ só acendem a partir do 2º snapshot da curva (D+2). */}
      {longaLive && curvas.d1Pendente && (
        <div
          className="text-[11px] text-amber-200/80 bg-amber-400/5 border border-amber-300/20 rounded-md px-3 py-1.5"
          data-testid="curva-pnl-aguardando-snapshot"
        >
          Δ aguardando 2º snapshot da curva (acende em D+2). D0 e
          MWm/MWh já estão vivos.
        </div>
      )}

      {/* [C] Curva curta (#55: vivo de /api/curva-pnl). GO 03/08: meses fora
          da cobertura do feed (2027+ = granularidade anual) saem "—" com nota. */}
      <CurvaTable
        titulo="Curva Curta (mensal)"
        rows={curtaRows}
        nota={curvas.data.curta.nota_cobertura}
      />

      {/* [D] Curva longa (#55 vivo) — #228 item 5: PADRONIZADA com a Curta
          (mesmas colunas, sem PnL; o FV agregado já vive na ResultadoTable). */}
      <CurvaTable
        titulo="Curva Longa (anual)"
        rows={longa.linhas}
        totalMwh={longa.total_mwh}
      />

      {/* [D2] #34: decomposição da posição por tipo × submercado — card ADITIVO
          (as CurvaTables acima seguem intocadas; isto é a "forma do risco"). */}
      <PosicaoDecomposta />

      {/* [E] DV01 por ano (#29) — decomposição carrego / curva / surpresa por ano
          de suprimento (layout do mock). Vivo de /api/curva-pnl/dv01-ipca; "—"
          honesto enquanto carrega/erro (estrutura do mock como placeholder). */}
      <div className="bg-indra-azul-surface rounded-lg border border-white/10 p-4" data-testid="dv01-por-ano">
        <h3 className="text-white font-semibold text-sm mb-1 flex items-center gap-2">
          DV01 por ano
          {/* #60: as-of do DADO servido (snapshot diário / live) */}
          {dv01 && <AsOfBadge asOf={dv01.as_of ?? null} />}
        </h3>
        <p className="text-white/40 text-[11px] mb-3">
          decomposição resultado-por-IPCA · carrego (R$, esperado) · DV01 curva (R$/bp) · surpresa (R$/bp)
        </p>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="text-left py-2 px-3 text-xs uppercase tracking-wide text-white/60 bg-indra-azul-deep">Ano</th>
                <th className="text-right py-2 px-3 text-xs uppercase tracking-wide text-white/60 bg-indra-azul-deep">Carrego (próx. mês)</th>
                <th className="text-right py-2 px-3 text-xs uppercase tracking-wide text-white/60 bg-indra-azul-deep">DV01 curva (R$/bp)</th>
                <th className="text-right py-2 px-3 text-xs uppercase tracking-wide text-white/60 bg-indra-azul-deep">Surpresa (R$/bp)</th>
              </tr>
            </thead>
            <tbody>
              {dv01 ? (
                dv01.por_ano.map((row) => (
                  <tr key={row.ano} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-2 px-3 text-sm text-white">{row.ano}</td>
                    <td className={"py-2 px-3 text-right text-sm font-mono tabular-nums " + corDv01(row.carrego)}>{fmtBRLcompactSigned(row.carrego)}</td>
                    <td className={"py-2 px-3 text-right text-sm font-mono tabular-nums " + corDv01(row.dv01_curva)}>{fmtBRLcompactSigned(row.dv01_curva)}</td>
                    <td className={"py-2 px-3 text-right text-sm font-mono tabular-nums " + corDv01(row.surpresa)}>{fmtBRLcompactSigned(row.surpresa)}</td>
                  </tr>
                ))
              ) : (
                // Fail-closed: sem dv01 (carregando/erro) → "—" sem inventar
                // anos do mock (a lista de anos também é dado).
                <tr>
                  <td colSpan={4} className="py-3 px-3 text-center text-sm text-slate-500">
                    —
                  </td>
                </tr>
              )}
            </tbody>
            {dv01 && (
              <tfoot>
                <tr className="border-t border-white/15">
                  <td className="py-2 px-3 text-sm text-white/70">Total</td>
                  <td className={"py-2 px-3 text-right text-sm font-mono tabular-nums " + corDv01(dv01.carrego_total)}>{fmtBRLcompactSigned(dv01.carrego_total)}</td>
                  <td className={"py-2 px-3 text-right text-sm font-mono tabular-nums " + corDv01(dv01.dv01_curva_total)}>{fmtBRLcompactSigned(dv01.dv01_curva_total)}</td>
                  <td className={"py-2 px-3 text-right text-sm font-mono tabular-nums " + corDv01(dv01.surpresa_total)}>{fmtBRLcompactSigned(dv01.surpresa_total)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* #29: expectativa de inflação do próx. mês (base do carrego) + legenda
            explicativa pros sócios — usa os metadados que o endpoint já devolve. */}
        {dv01 && (
          <p className="mt-3 text-[11px] text-white/50" data-testid="dv01-expectativa">
            Expectativa de inflação do próximo mês (base do carrego):{" "}
            <b className="text-white/70 font-mono">
              {dv01.metadados.implicita_mensal_prox_pct >= 0 ? "+" : ""}
              {fmtNum(dv01.metadados.implicita_mensal_prox_pct, 3)}%
            </b>{" "}
            · fonte: inflação implícita ANBIMA (curva pré × NTN-B, #218)
            {dv01.metadados.curva_implicita_asof
              ? ` · as-of ${brDate(dv01.metadados.curva_implicita_asof)}`
              : ""}
            .
          </p>
        )}
        <div className="mt-3 space-y-1 text-[11px] text-white/40 leading-relaxed" data-testid="dv01-legenda">
          <p>
            <b className="text-white/60">Como ler</b> (sinal + = a favor da Indra): o varejo vende
            contratos reajustados por IPCA, então a carteira ganha valor quando a inflação sobe — a
            Indra é &quot;comprada em inflação&quot;.
          </p>
          <p>
            <b className="text-white/60">Carrego (próx. mês):</b> quanto a carteira engorda no próximo
            mês se a inflação vier na expectativa (inflação implícita ANBIMA). É o ganho que o tempo
            traz — determinístico, não depende de surpresa.
          </p>
          <p>
            <b className="text-white/60">DV01 curva (R$/bp):</b> quanto o valor muda se a expectativa
            de inflação (curva implícita) subir 1 ponto-base (0,01%). Mede a sensibilidade ao nível da
            curva.
          </p>
          <p>
            <b className="text-white/60">Surpresa (R$/bp):</b> quanto o valor muda se o IPCA realizado
            do próximo mês vier 1 ponto-base acima do esperado (implícita ANBIMA). É o risco do
            realizado diferir da projeção.
          </p>
          <p className="text-white/30">
            Medidas distintas, não somáveis: o <b className="text-white/50">carrego é R$</b> (P&amp;L
            esperado do mês), enquanto <b className="text-white/50">DV01 curva e surpresa são R$/bp</b>
            {" "}(sensibilidades — multiplique pelo movimento em bp pra virar R$).
          </p>
        </div>
      </div>

      {/* #84: FV, Duration e Curva Longa — universo declarado pela rota. */}
      <CoberturaHint cobertura={curvas.data.cobertura} sujeito="desta curva" />
    </div>
  );
}
